import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { InceptionTypeInputEnum } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { useCreateProduct } from '@/modules/admin-center/hooks/useCreateProduct'
import { useCreateInvoiceLineItem } from '@/modules/invoices/hooks/useCreateInvoiceLineItem'
import { useCreateOrUpdatePartyProduct } from '@/modules/products/hooks/useCreateOrUpdatePartyProduct'
import { CreateProductFormInputs } from '@/modules/shared/components/create-product-modal/CreateProductFormSchema'
import { UseCreateAndAddProductProps } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductRequisition'
import { displayToast } from '@/modules/shared/components/toast/displayToast'
import { useAlert } from '@/modules/shared/hooks/useAlert'

export function useCreateAndAddProductInvoice({ quickAdd, refetch, onCloseModal, id }: UseCreateAndAddProductProps) {
  const { currentOrganisation } = useSessionUser()
  const { t } = useTranslation()
  const [createProduct, { loading: createProductLoading }] = useCreateProduct()
  const [createInvoiceLineItem, { loading: createInvoiceLineLoading }] = useCreateInvoiceLineItem()

  const navigate = useNavigate()
  const { alertDialog } = useAlert()

  const [createOrUpdatePartlyProduct] = useCreateOrUpdatePartyProduct()

  const onCreateAndAddProductInvoice = (
    input: CreateProductFormInputs,
    isAPurchaser?: boolean,
    image?: File | null
  ) => {
    const {
      categoryId,
      brand,
      itemDescription,
      unitPrice,
      quantity,
      itemMeasure,
      itemSize,
      itemPackName,
      itemSellPackName,
      itemSellQuantity,
      taxPercentage,
      departmentId,
      accountId,
      code,
      externalId,
    } = input
    createProduct({
      variables: {
        input: {
          categoryId,
          brand,
          itemDescription,
          locale: 'en',
          itemMeasure,
          itemSize,
          itemPackName: itemPackName === 'null' ? null : itemPackName,
          itemSellPackName,
          itemSellQuantity,
          externalId,
          inceptionType: InceptionTypeInputEnum.Manual,
          ownerCode: isAPurchaser ? code : undefined,
          image,
        },
      },
      onCompleted: async (data) => {
        const createdProductId = Number(data.createProduct.product?.id)
        if (code) {
          await createOrUpdatePartlyProduct({
            variables: {
              input: {
                productId: createdProductId,
                organisationId: Number(currentOrganisation?.id),
                code: code,
              },
            },
          })
        }
        createInvoiceLineItem({
          variables: {
            input: {
              productId: createdProductId,
              taxPercentage: Number(taxPercentage),
              invoiceId: Number(id),
              invoicePrice: unitPrice,
              quantity: Number(quantity),
              departmentId,
              accountId,
            },
          },
          onCompleted: () => {
            if (quickAdd) {
              onCloseModal && onCloseModal()
              displayToast({ title: t('general.productAdded', 'Product Added') })
              refetch?.()
            } else {
              navigate(generatePath('/invoices/:invoiceId/summary', { invoiceId: String(id) }))
            }
          },
        })
      },
      onError() {
        alertDialog({ type: 'error' })
      },
    })
  }

  return {
    onCreateAndAddProductInvoice,
    loading: createProductLoading || createInvoiceLineLoading,
  }
}
